import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  TCheckboxChangeEvent,
  TShareLinkModalTableCellProps,
} from '@components/ShareLinkModal/ShareLinkModal.types';
import { useStoreActions } from 'easy-peasy';
import {
  deleteJobClientSelector,
  putJobClientSelector,
} from '@model/Job/Job.selectors';
import {
  ACTION_VARIANTS,
  Button,
  Checkbox,
  ICON_NAMES,
} from '@reverse-hr/design-system';
import { useUserPackageVisibility } from '@components/ShareLinkModal/_hooks/use-user-package-visibility';

export const ShareLinkModalTableCell: FC<
  TShareLinkModalTableCellProps
> = props => {
  const { index, item, jobId, isMainClient, setAlertDetail } = props;
  const userPackageVisibility = useUserPackageVisibility();

  const [isLoading, setIsLoading] = useState(false);

  const putClient = useStoreActions(putJobClientSelector);
  const deleteClient = useStoreActions(deleteJobClientSelector);

  const isActionDisabled =
    isLoading || isMainClient || !userPackageVisibility.isEditable;

  const { t } = useTranslation('', {
    keyPrefix: 'modal.share',
  });

  const { t: tCommon } = useTranslation('', {
    keyPrefix: 'common',
  });

  const handleCompensationVisibilityChange = async (
    event: TCheckboxChangeEvent,
    clientId: string,
  ): Promise<void> => {
    if (!jobId) {
      return;
    }

    try {
      setIsLoading(true);
      await putClient({
        jobId,
        clientId,
        isCompensationPackageVisible: event.isChecked,
      });
    } catch (error) {
      if (setAlertDetail) {
        setAlertDetail({
          visible: true,
          type: 'negative',
          icon: 'icn-close-big',
          children: t('alert.negative'),
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClient = async (clientId: string): Promise<void> => {
    if (!jobId) {
      return;
    }

    try {
      setIsLoading(true);
      await deleteClient({ jobId, clientId });
    } catch (error) {
      if (setAlertDetail) {
        setAlertDetail({
          visible: true,
          type: 'negative',
          icon: 'icn-close-big',
          children: t('alert.negative'),
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <tr key={index} className="c-share-link-modal__table__row">
      <td className="c-share-link-modal__table__cell">
        {tCommon('formatting.fullName', {
          firstName: item.clientFirstName,
          lastName: item.clientLastName,
        })}
        <span className="c-share-link-modal__table__cell-recipient-type">
          {isMainClient ? t('table.mainClient') : t('table.guest')}
        </span>
      </td>
      <td className="c-share-link-modal__table__cell">
        <p>{item.clientEmail}</p>
      </td>
      {userPackageVisibility.isVisible && (
        <>
          <td className="c-share-link-modal__table__cell">
            <Checkbox
              id={item.clientId}
              name={item.clientId}
              isChecked={item.isCompensationPackageVisible}
              isDisabled={isActionDisabled}
              onChange={event =>
                handleCompensationVisibilityChange(
                  event,
                  item.clientId as string,
                )
              }
            />
          </td>
          <td className="c-share-link-modal__table__cell">
            <Button
              className="c-share-link-modal__table__remove"
              variant={ACTION_VARIANTS.SIMPLE_TEXT}
              rightIcon={ICON_NAMES.TRASH_FULL}
              isDisabled={isActionDisabled}
              onClick={() => handleDeleteClient(item.clientId as string)}
            />
          </td>
        </>
      )}
    </tr>
  );
};
